import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import type { EventType, TransactionDetails } from '@alafec/shared';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { AllTransactionsTable } from '../components/transactions/all-transactions-table';
import { TransactionsToolbar } from '../components/transactions/transactions-toolbar';
import { TransactionDetailDrawer } from '../components/transactions/transaction-detail-drawer';
import {
  ClassifyDialog,
  type ClassifyDialogPrefill,
  type ClassifyDialogEvent,
} from '../components/transactions/classify-dialog';
import {
  useAllTransactions,
  useAssets,
  useChains,
  useTransactionStatusCounts,
  useWallets,
} from '../lib/api/hooks/use-queries';
import {
  useApplyOverride,
  useBulkIgnore,
  useReclassifyAll,
  useRevertToAuto,
} from '../lib/api/hooks/use-mutations';
import {
  DEFAULT_FILTER,
  DEFAULT_SORT,
  fiscalYearRange,
  type TxFilterState,
  type TxSortKey,
  type TxSortState,
  type TxStatusFilter,
} from '../lib/transactions/filter';
import { EVENT_TYPES } from '@alafec/shared';
import { eventTypeLabel } from '../lib/event-types';
import { todayIso } from '../lib/dates';
import { useDebouncedValue } from '../lib/use-debounced-value';
import { toast } from '../components/ui/use-toast';

const PAGE_SIZE = 50;

const EVENT_TYPE_OPTIONS = EVENT_TYPES.map((value) => ({
  value,
  label: eventTypeLabel(value),
})).toSorted((a, b) => a.label.localeCompare(b.label));

export interface TransactionsSearch {
  readonly rawTxId?: number;
}

function parseTransactionsSearch(search: Record<string, unknown>): TransactionsSearch {
  const value = search['rawTxId'];
  if (value === undefined || value === null || value === '') return {};
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? { rawTxId: parsed } : {};
}

function currentMonthRange(today: string): { from: string; to: string } {
  const parts = today.split('-').map(Number);
  const y = parts[0] ?? 1970;
  const m = parts[1] ?? 1;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, '0');
  return { from: `${y}-${mm}-01`, to: `${y}-${mm}-${String(last).padStart(2, '0')}` };
}

function TransactionsIndexPage(): JSX.Element {
  const search = parseTransactionsSearch(Route.useSearch() as Record<string, unknown>);
  const [filter, setFilter] = useState<TxFilterState>(DEFAULT_FILTER);
  const [sort, setSort] = useState<TxSortState>(DEFAULT_SORT);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [selectedRawTxId, setSelectedRawTxId] = useState<number | null>(search.rawTxId ?? null);
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  const [clearOverrides, setClearOverrides] = useState(false);
  const [bulkIgnoreOpen, setBulkIgnoreOpen] = useState(false);
  const [bulkIgnoreReason, setBulkIgnoreReason] = useState('');
  const [bulkIgnoreError, setBulkIgnoreError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{
    rawTxId: number;
    walletId: number;
    occurredAt: number;
    prefill?: ClassifyDialogPrefill;
    events: readonly ClassifyDialogEvent[];
  } | null>(null);

  useEffect(() => {
    setSelectedRawTxId(search.rawTxId ?? null);
  }, [search.rawTxId]);

  // Tous les filtres sont appliqués côté serveur, avant pagination — un filtre
  // ne doit jamais cacher une transaction située sur une autre page.
  const debouncedSearch = useDebouncedValue(filter.search.trim());
  const transactions = useAllTransactions({
    limit: PAGE_SIZE,
    sortBy: sort.key,
    sortDir: sort.dir,
    offset: page * PAGE_SIZE,
    status: filter.status,
    ...(filter.chainId !== null ? { chainId: filter.chainId } : {}),
    ...(filter.walletId !== null ? { walletId: filter.walletId } : {}),
    ...(filter.assetSymbol !== null ? { assetSymbol: filter.assetSymbol } : {}),
    ...(filter.eventType !== null ? { eventType: filter.eventType } : {}),
    ...(filter.dateFrom !== null ? { dateFrom: filter.dateFrom } : {}),
    ...(filter.dateTo !== null ? { dateTo: filter.dateTo } : {}),
    ...(debouncedSearch !== '' ? { search: debouncedSearch } : {}),
  });
  const chainsQuery = useChains();
  const assets = useAssets();
  const wallets = useWallets();
  const statusCounts = useTransactionStatusCounts(filter.chainId ?? undefined);
  const override = useApplyOverride();
  const bulkIgnore = useBulkIgnore();
  const reclassify = useReclassifyAll();
  const revert = useRevertToAuto();

  const pageRows = transactions.data?.rows ?? [];
  const total = transactions.data?.total ?? 0;
  const busy =
    override.isPending || bulkIgnore.isPending || reclassify.isPending || revert.isPending;

  const today = todayIso();
  const visible = pageRows;

  const chainOptions = useMemo(
    () => (chainsQuery.data ?? []).map((c) => ({ id: c.id, name: c.name })),
    [chainsQuery.data],
  );
  const walletOptions = useMemo(
    () =>
      (wallets.data ?? [])
        .map((w) => ({ id: w.id, label: w.label }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    [wallets.data],
  );
  const assetOptions = useMemo(
    () =>
      Array.from(new Set((assets.data ?? []).map((a) => a.symbol))).toSorted((a, b) =>
        a.localeCompare(b),
      ),
    [assets.data],
  );

  // Tous les compteurs viennent du serveur ; repli sur le total de la requête
  // courante tant qu'ils ne sont pas chargés.
  const counts = useMemo<Partial<Record<TxStatusFilter, number>>>(() => {
    if (statusCounts.data !== undefined) return statusCounts.data;
    return { [filter.status]: total };
  }, [statusCounts.data, filter.status, total]);

  if (transactions.isLoading || wallets.isLoading) return <Skeleton className="h-48" />;

  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = page * PAGE_SIZE + pageRows.length;
  const allSelected = visible.length > 0 && visible.every((r) => selected.has(r.rawTxId));

  function goToPage(next: number): void {
    setPage(next);
    // La sélection est limitée à la page courante : on la vide en changeant de page.
    setSelected(new Set());
  }

  function onSort(key: TxSortKey): void {
    goToPage(0);
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'date' ? 'desc' : 'asc' },
    );
  }

  function onToggleRow(rawTxId: number): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rawTxId)) next.delete(rawTxId);
      else next.add(rawTxId);
      return next;
    });
  }

  function onToggleAll(): void {
    setSelected(allSelected ? new Set() : new Set(visible.map((r) => r.rawTxId)));
  }

  function onRevertToAuto(rawTxId: number, walletId: number): void {
    revert.mutate(
      { rawTxId, walletId },
      {
        onSuccess: () => toast({ title: 'Transaction repassée en classement automatique' }),
      },
    );
  }

  async function onBulkIgnore(): Promise<void> {
    const reason = bulkIgnoreReason.trim();
    if (reason.length < 10 || reason.length > 500) {
      setBulkIgnoreError('Le motif doit contenir entre 10 et 500 caractères.');
      return;
    }
    const items = pageRows
      .filter((row) => selected.has(row.rawTxId))
      .map((row) => ({ rawTxId: row.rawTxId, walletId: row.walletId }));
    if (items.length !== selected.size) {
      setBulkIgnoreError('Une transaction sélectionnée ne peut pas être rattachée à son wallet.');
      return;
    }
    let result;
    try {
      result = await bulkIgnore.mutateAsync({ items, reason });
    } catch {
      return;
    }
    setBulkIgnoreOpen(false);
    setBulkIgnoreReason('');
    setBulkIgnoreError(null);
    const problems = [...result.conflicts, ...result.failed];
    if (problems.length === 0) {
      toast({ title: `${result.succeeded.length} transaction(s) ignorée(s)` });
      setSelected(new Set());
      return;
    }
    const conflictNote =
      result.conflicts.length > 0 ? ` dont ${result.conflicts.length} en période close` : '';
    toast({
      title: `${result.succeeded.length} ignorée(s), ${problems.length} en échec${conflictNote}`,
      variant: 'destructive',
    });
    setSelected(new Set(problems.map((p) => p.rawTxId)));
  }

  async function onConfirmReclassify(): Promise<void> {
    const walletIds = (wallets.data ?? []).map((w) => w.id);
    if (walletIds.length === 0) {
      toast({ title: 'Aucun wallet configuré', variant: 'destructive' });
      return;
    }
    setReclassifyOpen(false);
    const results = await Promise.allSettled(
      walletIds.map((walletId) => reclassify.mutateAsync({ walletId, clearOverrides })),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed > 0) {
      toast({
        title: `Re-classification : ${walletIds.length - failed} wallet(s) traité(s), ${failed} en échec`,
        variant: 'destructive',
      });
      return;
    }
    toast({
      title: clearOverrides
        ? 'Tout repassé en automatique (classements manuels inclus)'
        : 'Re-classification lancée (classements manuels conservés)',
      ...(walletIds.length > 1 ? { description: `${walletIds.length} wallets traités` } : {}),
    });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Transactions</h1>
        <p className="text-sm text-muted-foreground">
          {total} transaction(s){filter.status !== 'all' ? ' pour ce filtre' : ''}
        </p>
      </div>

      <TransactionsToolbar
        filter={filter}
        onFilterChange={(patch) => {
          setFilter((prev) => ({ ...prev, ...patch }));
          // Chaque filtre relance une requête serveur : on repart page 1.
          setPage(0);
          setSelected(new Set());
        }}
        counts={counts}
        chains={chainOptions}
        wallets={walletOptions}
        assets={assetOptions}
        eventTypes={EVENT_TYPE_OPTIONS}
        fiscalYear={fiscalYearRange(today)}
        thisMonth={currentMonthRange(today)}
        onReclassify={() => {
          setClearOverrides(false);
          setReclassifyOpen(true);
        }}
        busy={busy}
        selectionCount={selected.size}
        onBulkIgnore={() => {
          setBulkIgnoreReason('');
          setBulkIgnoreError(null);
          setBulkIgnoreOpen(true);
        }}
        onClearSelection={() => setSelected(new Set())}
      />

      <Card>
        <CardContent className="pt-6">
          <AllTransactionsTable
            chains={chainsQuery.data}
            rows={visible}
            sort={sort}
            onSort={onSort}
            selected={selected}
            onToggleRow={onToggleRow}
            onToggleAll={onToggleAll}
            allSelected={allSelected}
            showWallet={walletOptions.length > 1}
            busyRowId={revert.isPending ? (revert.variables?.rawTxId ?? null) : null}
            onRevert={(row) => onRevertToAuto(row.rawTxId, row.walletId)}
            onExamine={(row) => setSelectedRawTxId(row.rawTxId)}
          />
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {total === 0 ? 'Aucune transaction' : `${rangeStart}–${rangeEnd} sur ${total}`}
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page === 0 || transactions.isFetching}
                onClick={() => goToPage(page - 1)}
              >
                Précédent
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={rangeEnd >= total || transactions.isFetching}
                onClick={() => goToPage(page + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <TransactionDetailDrawer
        rawTxId={selectedRawTxId}
        open={selectedRawTxId !== null}
        onOpenChange={(open) => !open && setSelectedRawTxId(null)}
        isSubmitting={revert.isPending}
        onRevert={(rawTxId, walletId) => {
          onRevertToAuto(rawTxId, walletId);
          setSelectedRawTxId(null);
        }}
        onEdit={(details: TransactionDetails) => {
          const first = details.currentEvents[0];
          const prefill: ClassifyDialogPrefill | undefined = first
            ? {
                eventType: first.eventType as EventType,
                assetId: first.assetId,
                amount: first.amount,
                ...(first.tag !== undefined ? { tag: first.tag } : {}),
              }
            : undefined;
          setEditing({
            rawTxId: details.rawTxId,
            walletId: details.walletId,
            occurredAt: details.blockTimestamp,
            events: details.currentEvents
              .filter((event) => event.walletId === details.walletId)
              .map((event) => Object.assign({}, event, { tag: event.tag ?? 'DEFAULT' })),
            ...(prefill !== undefined ? { prefill } : {}),
          });
          setSelectedRawTxId(null);
        }}
      />

      <Dialog open={reclassifyOpen} onOpenChange={setReclassifyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Re-classifier toutes les transactions</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Réapplique les heuristiques automatiques à toutes les transactions.
            </p>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 cursor-pointer accent-foreground"
                checked={clearOverrides}
                onChange={(e) => setClearOverrides(e.target.checked)}
              />
              <span>
                Inclure les classements manuels (les <strong>repasser en automatique</strong>).
                Action irréversible sur les overrides existants.
              </span>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setReclassifyOpen(false)} disabled={busy}>
              Annuler
            </Button>
            <Button onClick={() => void onConfirmReclassify()} disabled={busy}>
              Lancer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={bulkIgnoreOpen}
        onOpenChange={(open) => {
          setBulkIgnoreOpen(open);
          if (!open) setBulkIgnoreError(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ignorer les transactions sélectionnées</DialogTitle>
          </DialogHeader>
          <label className="space-y-2 text-sm">
            <span className="font-medium">Motif de l’exclusion groupée</span>
            <textarea
              aria-label="Motif de l’exclusion groupée"
              className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2"
              maxLength={500}
              value={bulkIgnoreReason}
              onChange={(event) => {
                setBulkIgnoreReason(event.target.value);
                setBulkIgnoreError(null);
              }}
            />
          </label>
          {bulkIgnoreError !== null ? (
            <p className="text-sm text-destructive" role="alert">
              {bulkIgnoreError}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setBulkIgnoreOpen(false)} disabled={busy}>
              Annuler
            </Button>
            <Button onClick={() => void onBulkIgnore()} disabled={busy}>
              Confirmer l’exclusion
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {editing !== null ? (
        <ClassifyDialog
          rawTxId={editing.rawTxId}
          occurredAt={editing.occurredAt}
          events={editing.events}
          {...(editing.prefill !== undefined ? { prefill: editing.prefill } : {})}
          open
          onOpenChange={(open) => !open && setEditing(null)}
          submitting={override.isPending}
          assets={(assets.data ?? []).map((a) => ({ id: a.id, symbol: a.symbol }))}
          onSubmit={(payload) => {
            const id = editing.rawTxId;
            override.mutate(
              { rawTxId: id, walletId: editing.walletId, payload },
              {
                onSuccess: () => {
                  toast({ title: 'Classification appliquée' });
                  setEditing(null);
                },
              },
            );
          }}
        />
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/transactions/')({
  validateSearch: parseTransactionsSearch,
  component: TransactionsIndexPage,
});
